import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { BusinessContract, ContractSignatory, SignatoryRole } from '@/types/business-opportunities';
import { addContractSignatory } from '@/services/business-opportunities';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(3, 'O nome deve ter pelo menos 3 caracteres'),
  email: z.string().email('Email inválido'),
  title: z.string().optional(),
  company: z.string().optional(),
  role: z.enum(['contractor', 'contractee', 'witness']),
});

type FormValues = z.infer<typeof formSchema>;

type AddSignatoryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: BusinessContract;
  onSignatoryAdded: (signatory: ContractSignatory) => void;
};

const roleLabels = {
  contractor: 'Contratante',
  contractee: 'Contratada',
  witness: 'Testemunha',
};

const AddSignatoryDialog: React.FC<AddSignatoryDialogProps> = ({
  open,
  onOpenChange,
  contract,
  onSignatoryAdded,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      title: '',
      company: '',
      role: 'contractee',
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      
      const signatoryData: Partial<ContractSignatory> = {
        contract_id: contract.id,
        name: data.name,
        email: data.email,
        title: data.title || undefined,
        company: data.company || undefined,
        role: data.role as SignatoryRole,
      };

      console.log('Adding signatory with data:', signatoryData);
      const newSignatory = await addContractSignatory(signatoryData);
      console.log('New signatory added:', newSignatory);
      
      onSignatoryAdded(newSignatory);
      onOpenChange(false);
      form.reset();

      toast({
        title: 'Signatário adicionado',
        description: 'O signatário foi adicionado com sucesso ao contrato.',
      });
    } catch (error) {
      console.error('Error adding signatory:', error);
      toast({
        title: 'Erro ao adicionar signatário',
        description: `Não foi possível adicionar o signatário: ${(error as any)?.message || 'Erro desconhecido'}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Adicionar Signatário</DialogTitle>
          <DialogDescription>
            Adicione um signatário ao contrato {contract.title}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input placeholder="Nome completo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input 
                      type="email" 
                      placeholder="email@exemplo.com" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cargo</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Diretor, Gerente" {...field} />
                    </FormControl>
                    <FormDescription>
                      Cargo ou posição do signatário
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empresa</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome da empresa" {...field} />
                    </FormControl>
                    <FormDescription>
                      Empresa que o signatário representa
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Papel no Contrato *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o papel" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="contractor">Contratante</SelectItem>
                      <SelectItem value="contractee">Contratada</SelectItem>
                      <SelectItem value="witness">Testemunha</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Define o papel do signatário neste contrato
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adicionando...
                  </>
                ) : (
                  'Adicionar Signatário'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSignatoryDialog; 